import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { UPDATE_ORDER_DESCRIPTION, UPDATE_ORDER_INFO } from './constants';
import {
  ConfirmUpdateButton,
  DialogActionsStyled,
  DialogCancelButton,
  DialogDescription,
  DialogFields,
  EditableTextFieldStyled,
  FieldLabel,
  FieldWrapper,
  InfoBanner,
  RequiredMark,
  TextFieldStyled,
} from './styled';
import { UpdateOrderIdDialogProps } from './types';

const UpdateOrderIdDialog = ({ currentConnectOrderId, open, onClose, onSubmit }: UpdateOrderIdDialogProps) => {
  const [connectOrderId, setConnectOrderId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConnectOrderId('');
      setReason('');
      setSubmitting(false);
    }
  }, [open]);

  const canSubmit = Boolean(connectOrderId.trim()) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      await onSubmit({
        connectOrderId: connectOrderId.trim(),
        reason: reason.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: '560px', width: '100%' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Update Connect Order ID
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <DialogDescription>{UPDATE_ORDER_DESCRIPTION}</DialogDescription>

        <DialogFields>
          <FieldWrapper>
            <FieldLabel>Current Connect Order ID</FieldLabel>
            <TextFieldStyled disabled value={currentConnectOrderId || ''} size="small" />
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel>
              New Connect Order ID
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <EditableTextFieldStyled
              autoFocus
              placeholder="e.g. ord_abc12345"
              size="small"
              value={connectOrderId}
              onChange={(event) => setConnectOrderId(event.target.value)}
              inputProps={{ 'aria-label': 'New Connect Order ID' }}
            />
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel>Reason</FieldLabel>
            <EditableTextFieldStyled
              placeholder="e.g. Wrong Connect package"
              size="small"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ 'aria-label': 'Reason' }}
            />
          </FieldWrapper>
        </DialogFields>

        <InfoBanner>
          <InfoOutlinedIcon />
          <span>{UPDATE_ORDER_INFO}</span>
        </InfoBanner>
      </DialogContent>

      <DialogActionsStyled>
        <DialogCancelButton onClick={onClose} disabled={submitting}>
          Cancel
        </DialogCancelButton>
        <ConfirmUpdateButton
          variant="contained"
          startIcon={<SwapHorizIcon />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Update
        </ConfirmUpdateButton>
      </DialogActionsStyled>
    </Dialog>
  );
};

export default UpdateOrderIdDialog;
